import React, { useCallback, useEffect, useState } from 'react';
import { getTeamNameAvailability } from '../../../api';
import { Grid, Box, RadioGroup, Radio, FormControlLabel, Switch, Button, TextField, makeStyles, createStyles, Theme, Tooltip } from '@material-ui/core';
// import MoreVertIcon from '@material-ui/icons/MoreVert';
import HelpIcon from '@material-ui/icons/Help';
import { LoaderButton } from 'components/LoaderButton';
import { ModalWrapper } from 'components/Modals';
import { useDispatch, useSelector } from 'react-redux';
import { createTeam as createTeamThunk } from 'redux/thunks';
import { NewTeam } from 'redux/models/newTeam';
import { RootStore } from 'redux/store';
import './CreateTeamModal.scss';
import { GraphGroupUser } from 'redux/models/graphGroupUser';
import { GraphAutocomplete } from 'components/GraphAutocomplete';
import { resetCreateTeamSuccess } from 'redux/actions/team';

interface CreateTeamProps {
	open: boolean;
	hideBackdrop: boolean;
	allowClickOutsideToClose?: boolean;
	onBackClicked: () => void;
	onCloseClicked: () => void;
	onCreateTeamClicked?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		errorText: {
			color: theme.palette.error.main,
		},
	})
);

const CreateTeam: React.FC<CreateTeamProps> = (props: CreateTeamProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const creatingTeam = useSelector((state: RootStore) => state.team.creatingTeam);
	const creatingTeamSuccess = useSelector((state: RootStore) => state.team.creatingTeamSuccess);
	const currentUser = useSelector((state: RootStore) => state.user);
	const [teamName, setTeamName] = useState('');
	const [teamNameError, setTeamNameError] = useState('');
	const [users, setUsers] = React.useState<GraphGroupUser[]>([]);
	const [validating, setValidating] = useState(false);
	const [isPrivate, setIsPrivate] = useState(false);
	const [onlyAdminsCanPost, setOnlyAdminsCanPost] = useState(false);
	const { open, hideBackdrop, allowClickOutsideToClose, onBackClicked, onCloseClicked } = props;
	const privateTooltipText = 'Private groups will not be openly joinable. New members must be added by a current member to join';

	const onTeamNameChange = (event: any) => {
		setTeamName(event.target.value);
	};

	const onIsPrivateChange = () => {
		setIsPrivate(!isPrivate);
	};

	const onAdminsCanSendChange = () => {
		setOnlyAdminsCanPost(!onlyAdminsCanPost);
	};

	const selectUser = (users: any) => {
		setUsers(users);
	};

	const onFormSubmit = async (event: any) => {
		event.preventDefault();
		try {
			setValidating(true);

			if (teamName.length < 1) throw Error('Team name cannot be blank');
			let isAvailable = await getTeamNameAvailability(teamName);
			if (!isAvailable) throw Error('Team name is not available');

			const team: NewTeam = {
				teamName: teamName,
				isPrivate: isPrivate,
				adminOnlyPosts: onlyAdminsCanPost,
				usersOrGroups: users,
			};

			dispatch(createTeamThunk(team));
		} catch (error) {
			setTeamNameError(error.message || 'Invalid Input');
		} finally {
			setValidating(false);
		}
	};

	const resetForm = useCallback(() => {
		setTeamName('');
		setUsers([]);
		setIsPrivate(false);
		setOnlyAdminsCanPost(false);
		setTeamNameError('');
	}, []);

	useEffect(() => {
		if (open) {
			resetForm();
		}
	}, [open, resetForm]);

	useEffect(() => {
		if (creatingTeamSuccess) {
			dispatch(resetCreateTeamSuccess());
			onCloseClicked();
		}
	}, [creatingTeamSuccess, dispatch, onCloseClicked]);

	return (
		<ModalWrapper open={open} hideBackdrop={hideBackdrop} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
			<Box display="flex" flexWrap="wrap" alignItems="center" alignContent="center" flexDirection="column" className="create-team-box-layout">
				<div className="create-team-header">Create a new team</div>
				<Grid container item xs={10} justify="center" alignItems="flex-start" alignContent="flex-start">
					<form onSubmit={onFormSubmit}>
						<Grid className="create-team-fields" spacing={5} container item justify="space-evenly">
							<Grid item xs={12} sm={6}>
								<TextField
									id='create-team__team-name-input'
									fullWidth
									label="Team Name"
									variant="outlined"
									value={teamName}
									onChange={onTeamNameChange}
									error={teamNameError ? true : false}
								/>
								<div className={`create-team-input-subtext ${teamNameError ? classes.errorText : null}`}>{teamNameError || 'Required'}</div>
							</Grid>
							<Grid container item xs={12} sm={6} alignItems="center">
								<GraphAutocomplete currentUserId={currentUser.userId} onSelectUser={selectUser}></GraphAutocomplete>
							</Grid>
						</Grid>
						<Grid container item alignContent="center" className="create-team-permissions">
							<Grid container item>
								<Grid item>
									<div className="create-team-subheader">Token Permission</div>
									<RadioGroup aria-label="gender" name="gender1">
										<FormControlLabel
											value="All"
											control={
												<Radio
													name="token-permissions"
													checked={onlyAdminsCanPost === false}
													onChange={onAdminsCanSendChange}
													color="primary"
												/>
											}
											label="All members can send tokens"
										/>
										<FormControlLabel
											value="Admin"
											control={
												<Radio
													name="token-permissions"
													onChange={onAdminsCanSendChange}
													checked={onlyAdminsCanPost === true}
													color="primary"
												/>
											}
											label="Only admins can send tokens"
										/>
									</RadioGroup>
								</Grid>

								<Grid container item alignItems="center">
									<FormControlLabel
										checked={isPrivate}
										control={<Switch name="checkedB" color="primary" />}
										label="Make this group Private"
										className="create-team-switch-label"
										onChange={onIsPrivateChange}
									/>
									<Tooltip title={privateTooltipText}>
										<HelpIcon color="primary" fontSize="small" />
									</Tooltip>
								</Grid>
							</Grid>
						</Grid>
						<Grid className="form-action-buttons" container item justify="flex-end" alignItems="center" alignContent="center">
							<Grid item>
								<Button color="primary" className="create-team-back-button" onClick={onBackClicked}>
									Back
								</Button>
							</Grid>
							<Grid item className="create-team-button">
								<LoaderButton loading={creatingTeam || validating} type="submit">
									create team
								</LoaderButton>
							</Grid>
						</Grid>
					</form>
				</Grid>
			</Box>
		</ModalWrapper>
	);
};

export default CreateTeam;
